import { MacroData, SwitchButtonValue, ZoneCreationProperties } from '../types/zoneMapType';
import { FloorplanData, FloorplanFixtureData } from './zoneMapDataType';

/**
 * generate mapping from zoneName to key
 * key is generated by first 2 characters of zoneName
 * for example:
 *  Squash zone -> Sq
 *  Potatoes/Apples zone -> Po/Ap
 */
export function generateZoneNameKeyMap<T extends { zoneName: string }>(zoneData: T[]): Map<string, string> {
  const zoneNameKeyMap: Map<string, string> = new Map();
  zoneData.forEach((zone) => {
    const { zoneName } = zone;
    const subcategories = zoneName.split('/');
    const keys = subcategories.map((subcategory) => {
      const key = subcategory.slice(0, 2);
      return key;
    });

    zoneNameKeyMap.set(zoneName, keys.join('/'));
  });

  return zoneNameKeyMap;
}

/**
 * generate unique key for zoneName
 */
export function getZoneNameKey(zoneName: string, zoneNamekeyMap: Map<string, string>): string {
  return zoneNamekeyMap.get(zoneName) ?? '';
}

/**
 * Build mapping from fixtureId to fixture category data
 */
export const buildFixtureIdToCategoriesMap = (fixtureData: FloorplanData[]) => {
  const mapping: Record<
    string,
    {
      zoneName: string;
      zoneId: string;
      productTypes: string;
      isAgile: boolean;
      zoneNameKey: string;
      isDraft?: boolean | null;
    }
  > = {};
  fixtureData.forEach((fixture) => {
    const categories = fixture.categories?.sort((a, b) => a.localeCompare(b)).join(';') ?? '';
    if (!fixture.isAgile) {
      mapping[fixture.id] = {
        zoneName: categories,
        zoneId: '',
        productTypes: categories,
        isAgile: false,
        zoneNameKey: '',
        isDraft: false,
      };
    } else {
      mapping[fixture.id] = {
        zoneName: '',
        zoneId: '',
        productTypes: '',
        isAgile: true,
        zoneNameKey: '',
        isDraft: false,
      };
    }
  });
  return mapping;
};

/**
 * Build mapping from fixtureId to zone and fixture category data
 */
const buildFixtureIdToZoneDataMap = (
  data: MacroData[],
  fixtureData: FloorplanData[],
  zoneNamekeyMap: Map<string, string>,
) => {
  const mapping = buildFixtureIdToCategoriesMap(fixtureData);
  data.forEach((macroData) => {
    const { zoneName, zoneId, unParsedFixtureIds, concatnatedSubcategoryName, isDraft } = macroData;
    unParsedFixtureIds.forEach((fixtureId) => {
      mapping[fixtureId] = {
        zoneName,
        zoneId,
        productTypes: concatnatedSubcategoryName,
        isAgile: true,
        zoneNameKey: getZoneNameKey(zoneName, zoneNamekeyMap),
        isDraft,
      };
    });
  });
  return mapping;
};

/**
 * build mapping from fixtureId to editing zone data and fixture category data
 */
export const buildFixtureIdToEditingZoneDataMap = (
  data: Map<number, ZoneCreationProperties>,
  fixtureData: FloorplanData[],
  zoneNamekeyMap: Map<string, string>,
) => {
  const mapping = buildFixtureIdToCategoriesMap(fixtureData);
  Array.from(data.values()).forEach((zone) => {
    const { zoneName, subcategoryNames, fixtureIds } = zone;
    const concatnatedSubcategoryName = subcategoryNames.sort().join('/');
    fixtureIds.forEach((fixtureId) => {
      mapping[fixtureId] = {
        zoneName,
        zoneId: '',
        productTypes: concatnatedSubcategoryName,
        isAgile: true,
        zoneNameKey: getZoneNameKey(zoneName, zoneNamekeyMap),
      };
    });
  });
  return mapping;
};

/**
 * transform FloorplanFixtureData[] to FloorplanData[] which could be accepted by FP component
 */
export const transformFPData = (fixtures: FloorplanFixtureData[]): FloorplanData[] =>
  fixtures
    .filter(
      (fixture) =>
        fixture.model != null &&
        fixture.height != null &&
        fixture.width != null &&
        fixture.depth != null &&
        fixture.rotation != null &&
        fixture.translation != null,
    )
    .map((fixture) => ({
      id: fixture.id,
      model: fixture.model as string,
      height: fixture.height as number,
      width: fixture.width as number,
      depth: fixture.model?.includes('EURO_TABLE') ? 660 : (fixture.depth as number),
      rotation: fixture.rotation as number,
      translation: fixture.translation as number[],
      department: fixture.department ?? null,
      categories: fixture.categories ?? null,
      isAgile: fixture.isAgile ?? null,
    }));

/**
 * Validate editing zone data before publishing
 * 1.each zone must have at least one fixture
 * 2.fixtures in zone must be assigned with at least one product type
 * 3.each zone must have at least one product type and at least one fixture
 * 4.one fixture can only be assigned to one zone
 * 5.no duplicate zoneName across zones
 * 6.all agile fixtures must be assigned with at least one product type => no empty table
 */
export function validateEditingZoneData(
  zoneCreationProperties: Map<number, ZoneCreationProperties>,
  agileFixtures: FloorplanFixtureData[],
  isWholeFoods: boolean = false,
): Set<string> {
  const errors: Set<string> = new Set();
  const fixtureIdUsageMap: Map<string, boolean> = new Map();
  const zoneNames: Set<string> = new Set();
  const minZoneFacings = 1;
  const maxZoneFacings = 10;

  zoneCreationProperties.forEach((zoneCreationProperty) => {
    if (!isWholeFoods && zoneCreationProperty.subcategoryIds?.length === 0) {
      if (zoneCreationProperty.fixtureIds?.length === 0) {
        errors.add('All zones must have at least one product type and at least one fixture');
      } else {
        errors.add(
          `Zone with fixture(s) ${zoneCreationProperty.fixtureIds.toString()} must have at least one product type`,
        );
      }
    }
    if (zoneCreationProperty.fixtureIds?.length === 0) {
      errors.add(`Zone ${zoneCreationProperty.zoneName} must have at least one fixture`);
    }

    if (zoneCreationProperty.facings < minZoneFacings || zoneCreationProperty.facings > maxZoneFacings) {
      errors.add(
        `Zone ${zoneCreationProperty.zoneName} facings value must be from ${minZoneFacings} to ${maxZoneFacings}`,
      );
    }

    zoneCreationProperty.fixtureIds?.forEach((fixtureId) => {
      if (fixtureIdUsageMap.get(fixtureId)) {
        errors.add(`Agile fixture ${fixtureId} can only be assigned to a single zone`);
      }
      fixtureIdUsageMap.set(fixtureId, true);
    });

    if (zoneNames.has(zoneCreationProperty.zoneName)) {
      errors.add(`Zone name ${zoneCreationProperty.zoneName} cannot be used for multiple zones`);
    }
    zoneNames.add(zoneCreationProperty.zoneName);
  });

  if (!isWholeFoods) {
    agileFixtures.forEach((fixture) => {
      if (!fixtureIdUsageMap.get(fixture.id)) {
        errors.add(`Agile fixture ${fixture.id} must have a product type assigned`);
      }
    });
  }
  return errors;
}

/**
 * Validate publish zone date not empty
 */
export function validatePublishZoneLiveDate(switchButtonValue: string | undefined, copyToNewDate: string): Set<string> {
  const errors: Set<string> = new Set();
  if (switchButtonValue === SwitchButtonValue.CopyToNewMap && !copyToNewDate) {
    errors.add('Please select New Live Date for publishing zone map');
  }
  return errors;
}

/**
 * Filter out the fitxures that don't exist in agile fixtures
 */
export const filterFixtureIds = (fixtureIds: string[], agileFixtures: FloorplanFixtureData[]): string[] =>
  fixtureIds.filter((id) => agileFixtures.some((fixture) => fixture.id === id));

/**
 * When change the live date, Filter zone data from user input in zone creation side bar
 * For example:
 * user choose live date 08/22/2024 to create apple zones (assigning apple to table1 and table2)
 * then change live date to 01/09/2025, the previous input data should be filtered because
 * table1 doesn't exist on 01/09/2025
 */
export const filterZoneCreationProperties = (
  zoneCreationProperties: Map<number, ZoneCreationProperties>,
  agileFixtures: FloorplanFixtureData[],
): Map<number, ZoneCreationProperties> =>
  new Map(
    [...zoneCreationProperties].map(([zoneIndex, { fixtureIds, ...zoneCreationProperty }]) => [
      zoneIndex,
      {
        ...zoneCreationProperty,
        fixtureIds: filterFixtureIds(fixtureIds, agileFixtures),
      },
    ]),
  );

export default buildFixtureIdToZoneDataMap;
